import {
  zodToOpenaiJsonSchema
} from "./chunk-FQFJR6TD.mjs";

// types/interfaces/buttonSchema.ts
import { z } from "zod";
var ZapSchema = z.object({
  id: z.string(),
  title: z.string(),
  zdl: z.object({}).partial().passthrough(),
  paused: z.boolean(),
  relations: z.object({
    projects: z.array(z.string()),
    pages: z.array(z.string()),
    tables: z.array(z.string()),
    forms: z.array(z.string()),
    blockActions: z.array(z.string())
  })
}).passthrough();
var linkSchema = z.object({
  type: z.literal("navigate"),
  config: z.object({
    id: z.string(),
    pageId: z.string(),
    pageName: z.string(),
    sendQueryParams: z.boolean()
  })
});
var openExternalUrlSchema = z.object({
  type: z.literal("openExternalUrl"),
  config: z.object({
    id: z.string(),
    url: z.string(),
    sendQueryParams: z.boolean(),
    opensInNewTab: z.boolean()
  })
});
var ButtonBlockSchema = z.object({
  type: z.literal("button-block"),
  config: z.object({
    id: z.string(),
    label: z.string(),
    actions: z.array(z.union([linkSchema, openExternalUrlSchema]))
  })
});

// types/interfaces/fieldSchema.ts
import { z as z2 } from "zod";
var FieldBlockConfigBaseSchema = z2.object({
  id: z2.string(),
  name: z2.string(),
  nameAutoGenerated: z2.boolean(),
  required: z2.boolean(),
  hidden: z2.boolean(),
  label: z2.string(),
  placeholder: z2.string(),
  helpText: z2.string(),
  defaultValue: z2.string(),
  tablesFieldKey: z2.union([z2.string(), z2.null()]).describe(
    "If this connects to a table in the Existing Canvas, use the format ${{ tables._TABLE_1._FIELD_2 }} where _TABLE_1 is the table ID you want to connect and _FIELD_2 is the field ID you want to connect. If this connects to a table in the canvas this should reference the index of the field in the table via the format f0, f1, f2, "
  ),
  // Not including conditional logic for now
  readOnly: z2.boolean()
});
var FieldBlockTextInputSchema = FieldBlockConfigBaseSchema.extend({
  inputType: z2.literal("text")
});
var FieldBlockTextAreaInputSchema = FieldBlockConfigBaseSchema.extend({
  inputType: z2.literal("textarea")
});
var FieldBlockURLInputSchema = FieldBlockConfigBaseSchema.extend({
  inputType: z2.literal("url")
});
var FieldBlockDropDownConfigSchema = FieldBlockConfigBaseSchema.extend(
  {
    inputType: z2.literal("dropdown"),
    options: z2.string(),
    multiSelect: z2.boolean(),
    tableId: z2.string(),
    tableFieldId: z2.number().or(z2.string())
  }
);
var FieldBlockNumberConfigSchema = FieldBlockConfigBaseSchema.extend({
  inputType: z2.literal("number"),
  min: z2.string(),
  max: z2.string()
});
var FieldBlockYesNoConfigSchema = FieldBlockConfigBaseSchema.extend({
  inputType: z2.literal("yes-no")
});
var FieldBlockEmailConfigSchema = FieldBlockConfigBaseSchema.extend({
  inputType: z2.literal("email"),
  blockFreeEmailProviders: z2.boolean(),
  restrictDomains: z2.boolean(),
  allowedDomains: z2.string()
});
var DateFormatOptions = [
  {
    label: "YYYY-MM-DD",
    value: "yyyy-MM-dd"
  },
  {
    label: "MM-DD-YYYY",
    value: "MM-dd-yyyy"
  },
  {
    label: "DD-MM-YYYY",
    value: "dd-MM-yyyy"
  }
];
var FieldBlockDatepickerConfigSchema = FieldBlockConfigBaseSchema.extend({
  inputType: z2.literal("date-picker"),
  dateFormat: z2.literal("yyyy-MM-dd"),
  includeTime: z2.boolean(),
  defaultToNow: z2.literal(true)
});
var PhoneNumberFormatOptions = [
  {
    label: "(555) 555-1212",
    value: "phone_number_format_1"
  },
  {
    label: "+1 (555) 555-1212",
    value: "phone_number_format_2"
  },
  {
    label: "+44 55 1234 5678",
    value: "phone_number_format_3"
  }
];
var FieldBlockPhoneNumberConfigSchema = FieldBlockConfigBaseSchema.extend({
  inputType: z2.literal("phone-number"),
  phoneNumberFormat: z2.string(),
  defaultCountry: z2.string().length(2)
});
var FieldBlockCheckboxConfigSchema = FieldBlockConfigBaseSchema.extend(
  {
    inputType: z2.literal("checkbox"),
    label: z2.string(),
    text: z2.string(),
    defaultChecked: z2.boolean()
  }
);
var FieldBlockFileUploadSchema = FieldBlockConfigBaseSchema.extend({
  inputType: z2.literal("file-upload"),
  allowedFileTypes: z2.string(),
  allowedFileSize: z2.number()
});
var FieldBlockAIFormulaSchema = FieldBlockConfigBaseSchema.extend({
  inputType: z2.literal("ai-formula")
});
var FieldBlockMultipleLinkedRecordsSchema = FieldBlockConfigBaseSchema.extend({
  inputType: z2.literal("multiple-linked-record")
});
var FieldBlockLinkedRecordSchema = FieldBlockConfigBaseSchema.extend({
  inputType: z2.literal("linked-record")
});
var CurrencyFormatOptions = [
  {
    label: "Default ($20.11)",
    value: "usd1"
  },
  {
    label: "USD ($20.11)",
    value: "usd2"
  },
  {
    label: "Euro (20.11\u20AC)",
    value: "euro1"
  },
  {
    label: "Pound (\xA320.11)",
    value: "pound1"
  },
  {
    label: "Yen (\xA520.11)",
    value: "yen1"
  }
];
var CurrencyDecimalOptions = [
  {
    label: "00",
    value: 0
  },
  {
    label: "0.00",
    value: 2
  }
];
var FieldBlockCurrencyConfigSchema = FieldBlockConfigBaseSchema.extend(
  {
    inputType: z2.literal("currency"),
    decimals: z2.number(),
    currencyFormat: z2.string()
  }
);
var FieldBlockConfigSchema = z2.union([
  FieldBlockTextInputSchema,
  FieldBlockTextAreaInputSchema,
  FieldBlockURLInputSchema,
  FieldBlockDropDownConfigSchema,
  FieldBlockNumberConfigSchema,
  FieldBlockYesNoConfigSchema,
  FieldBlockEmailConfigSchema,
  FieldBlockDatepickerConfigSchema,
  FieldBlockPhoneNumberConfigSchema,
  FieldBlockCheckboxConfigSchema,
  FieldBlockFileUploadSchema,
  FieldBlockCurrencyConfigSchema,
  FieldBlockAIFormulaSchema,
  FieldBlockMultipleLinkedRecordsSchema,
  FieldBlockLinkedRecordSchema
]);
var FieldBlockSchema = z2.object({
  type: z2.literal("field-block"),
  config: FieldBlockTextInputSchema
});

// types/interfaces/interfaces.ts
import { z as z3 } from "zod";
var storageIdSchema = z3.string().describe(
  "To reference tables that exist in the canvas use their tableId. To reference tables that exist in the new outline use '${{ tables._TABLE_x }}'. "
);
var MarkdownTextBlockSchema = z3.object({
  type: z3.literal("markdown-text-block"),
  config: z3.object({
    id: z3.string(),
    content: z3.string().describe(
      "Use markdown formatting. Make sure to do backslash n for new lines. Use #s to denote different heading sizes."
    ),
    textAlign: z3.literal("center")
  })
}).describe(
  "A block for displaying formatted text content using markdown syntax"
);
var TriggerSchema = z3.object({
  type: z3.literal("notification"),
  config: z3.object({
    id: z3.string().describe(
      "ID defined as _ACTION_ID where ID is unique and starting at 1"
    ),
    //pageId: z.string(),
    message: z3.string(),
    sendQueryParams: z3.boolean()
  })
});
var FormBlockSchema = z3.object({
  type: z3.literal("form-block"),
  config: z3.object({
    id: z3.string(),
    submitText: z3.string(),
    storageType: z3.literal("tables"),
    storageId: z3.union([storageIdSchema, z3.null()]).describe(
      "To reference tables that exist in the existing canvas use their given tableId. To reference tables that exist in the new outline use '${{ tables._TABLE_x }}'. "
    )
  }),
  triggers: z3.union([z3.array(TriggerSchema), z3.null()]),
  children: z3.array(FieldBlockSchema)
}).describe(
  "A form block for collecting user input. Only one form block should exist per page. Must be connected to a Zapier Table. Cannot be included if there is no table to connect to."
);
var TableBlockSchema = z3.object({
  type: z3.literal("table-listing-block"),
  config: z3.object({
    id: z3.string(),
    title: z3.string(),
    tableId: z3.union([storageIdSchema, z3.null()]),
    newRecordButtonCopy: z3.string()
  })
}).describe(
  "A block for displaying and managing tabular data. Must be connected to a Zapier Table."
);
var KanbanColumnSchema = z3.object({
  value: z3.string().or(z3.number()),
  label: z3.string()
});
var KanbanBlockSchema = z3.object({
  type: z3.literal("kanban-block"),
  config: z3.object({
    id: z3.string(),
    title: z3.string(),
    tableId: z3.union([storageIdSchema, z3.null()]),
    groupFieldId: z3.number().or(z3.string()),
    cardTitleFieldId: z3.number().or(z3.string()),
    cardDescriptionFieldId: z3.number().or(z3.string()),
    cardDetailFields: z3.array(z3.number().or(z3.string())),
    selectedColumns: z3.array(KanbanColumnSchema),
    newCardButtonCopy: z3.string()
  })
}).describe(
  "A kanban board block for visual task management. Must be connected to a Zapier Table with appropriate field mappings."
);
var ChecklistBlockSchema = z3.object({
  type: z3.literal("checklist-block"),
  config: z3.object({
    id: z3.string(),
    title: z3.string(),
    tableId: z3.union([storageIdSchema, z3.null()]),
    listItemCompletedFieldId: z3.union([z3.number(), z3.string()]),
    listItemTitleFieldId: z3.union([z3.number(), z3.string()]),
    listItemDescriptionFieldId: z3.union([z3.number(), z3.string()]),
    listItemDetailFields: z3.array(z3.union([z3.number(), z3.string()]))
  })
}).describe(
  "A checklist block for managing task lists. Must be connected to a Zapier Table."
);
var LinkCardBlockSchema = z3.object({
  type: z3.literal("links-card-block"),
  config: z3.object({
    id: z3.string(),
    title: z3.string(),
    description: z3.string(),
    linkText: z3.string(),
    linkType: z3.enum(["page", "url"]),
    pageId: z3.string(),
    url: z3.string(),
    emoji: z3.string()
  })
}).describe(
  "A card component used within links-block. Must include title, description, call-to-action text, and URL."
);
var LinksBlockSchema = z3.object({
  type: z3.literal("links-block"),
  config: z3.object({
    id: z3.string(),
    headerTitle: z3.string()
  }),
  children: z3.array(LinkCardBlockSchema)
}).describe(
  "A block for displaying 2-10 link cards in a grid layout. Ideal for resource pages, contact pages, and about pages."
);
var SupportedBlockTypesEnum = z3.enum([
  "markdown-text-block",
  "form-block",
  "table-listing-block",
  "kanban-block",
  "checklist-block",
  "media-block",
  "links-block",
  "button-block",
  "divider-block"
]);
var MediaBlockSchema = z3.object({
  type: z3.literal("media-block"),
  config: z3.object({
    id: z3.union([z3.string(), z3.string()]),
    mediaType: z3.enum(["image", "video", "logo"]),
    mediaSource: z3.enum(["url", "upload"]),
    mediaUrl: z3.string(),
    height: z3.number(),
    alignContent: z3.enum(["left", "center", "right"])
  })
});
var ContentBlockSchema = z3.union([
  MarkdownTextBlockSchema,
  FormBlockSchema,
  TableBlockSchema,
  KanbanBlockSchema,
  ChecklistBlockSchema,
  MediaBlockSchema,
  LinksBlockSchema,
  ButtonBlockSchema
  // ChatbotBlockSchema,
  // DividerBlockSchema,
  // EmbedBlockSchema,
  // LayoutBlockSchema,
  // StripePaymentBlockSchema,
]);
var TemplatePageSchema = z3.object({
  id: z3.string(),
  name: z3.string(),
  title: z3.string().describe(
    "Provide a concise, action-oriented title (3-4 words) describing the page's purpose in the workflow. Use verbs like 'submit', 'add', or 'input' for pages with form fields."
  ),
  navigationEnabled: z3.boolean().describe("If more than one page, set this to true."),
  blocks: z3.array(ContentBlockSchema).describe(
    "Only one of the following can exist on each page: form-block, table-listing-block, kanban-block, checklist-block"
  ),
  slug: z3.string().describe("16 characters or less, alphanumeric").transform((val) => val.slice(0, 16)).transform((val) => val.replace(/[^a-zA-Z0-9]+$/, ""))
});
var TemplateInterfaceSchema = z3.object({
  id: z3.string(),
  name: z3.string().describe("Not to be longer than 32 characters").regex(/^[^&]+$/).transform((val) => val.slice(0, 32)),
  isHidden: z3.boolean(),
  pages: z3.array(TemplatePageSchema)
});
var interfaceOpenAiJsonSchema = zodToOpenaiJsonSchema(
  TemplateInterfaceSchema,
  "interfaces",
  {
    linkSchema,
    openExternalUrlSchema,
    ButtonBlockSchema,
    storageIdSchema
  }
);
var ModifiedFormBlockSchema = z3.object({
  type: z3.literal("form-block"),
  config: z3.object({
    id: z3.string(),
    submitText: z3.string(),
    storageType: z3.literal("tables"),
    storageId: z3.union([storageIdSchema, z3.null()]),
    triggers: z3.union([z3.array(TriggerSchema), z3.null()])
    // Triggers moved to config
  }),
  children: z3.array(FieldBlockSchema)
});
var ModifiedKanbanBlockSchema = z3.object({
  type: z3.literal("kanban-block"),
  config: z3.object({
    id: z3.string(),
    title: z3.string(),
    tableId: z3.union([storageIdSchema, z3.null()]),
    groupFieldId: z3.number().or(z3.string()),
    cardTitleFieldId: z3.number().or(z3.string()),
    cardDescriptionFieldId: z3.number().or(z3.string()),
    cardDetailFields: z3.array(z3.number().or(z3.string())),
    selectedColumns: z3.array(KanbanColumnSchema),
    // Added from field choices
    newCardButtonCopy: z3.string()
  })
});
var ModifiedContentBlockSchema = z3.union([
  MarkdownTextBlockSchema,
  ModifiedFormBlockSchema,
  TableBlockSchema,
  ModifiedKanbanBlockSchema,
  ChecklistBlockSchema,
  MediaBlockSchema,
  LinksBlockSchema,
  ButtonBlockSchema
]);
var ModifiedTemplatePageSchema = z3.object({
  id: z3.string(),
  name: z3.string(),
  title: z3.string(),
  navigationEnabled: z3.boolean(),
  blocks: z3.array(ModifiedContentBlockSchema),
  slug: z3.string().describe("16 characters or less, alphanumeric").transform((val) => val.slice(0, 16)).transform((val) => val.replace(/[^a-zA-Z0-9]+$/, ""))
});
var ModifiedTemplateInterfaceSchema = z3.object({
  id: z3.string(),
  name: z3.string(),
  isHidden: z3.boolean(),
  pages: z3.array(ModifiedTemplatePageSchema)
});

export {
  ZapSchema,
  linkSchema,
  openExternalUrlSchema,
  ButtonBlockSchema,
  FieldBlockConfigBaseSchema,
  FieldBlockDropDownConfigSchema,
  FieldBlockNumberConfigSchema,
  FieldBlockYesNoConfigSchema,
  FieldBlockEmailConfigSchema,
  DateFormatOptions,
  FieldBlockDatepickerConfigSchema,
  PhoneNumberFormatOptions,
  FieldBlockPhoneNumberConfigSchema,
  FieldBlockCheckboxConfigSchema,
  FieldBlockFileUploadSchema,
  FieldBlockAIFormulaSchema,
  FieldBlockMultipleLinkedRecordsSchema,
  FieldBlockLinkedRecordSchema,
  CurrencyFormatOptions,
  CurrencyDecimalOptions,
  FieldBlockCurrencyConfigSchema,
  FieldBlockConfigSchema,
  FieldBlockSchema,
  SupportedBlockTypesEnum,
  TemplateInterfaceSchema,
  interfaceOpenAiJsonSchema,
  ModifiedContentBlockSchema,
  ModifiedTemplateInterfaceSchema
};
